import type { JSX } from 'react';
import { Form, Input, Modal, Tag, Typography } from 'antd';
import type { FormInstance } from 'antd';

import type {
  AppointmentHistoryEntry,
  AppointmentServiceLine,
} from '@erp/appointments/api/appointments.api';
import SlateEditor from '@tools/components/slateEditor.tsx';
import { getRichTextPlainText } from '@tools/utils/richTextHtml.ts';

import type { HistoryFormValues, StaffOption } from './historyHelpers';

const NOTES_MAX_LENGTH = 2000;

const { Text } = Typography;

interface HistoryEntryFormModalProps {
  open: boolean;
  editingEntry: AppointmentHistoryEntry | null;
  form: FormInstance<HistoryFormValues>;
  staffOptions: StaffOption[];
  confirmLoading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  serviceLines?: AppointmentServiceLine[];
}

export function HistoryEntryFormModal({
  open,
  editingEntry,
  form,
  staffOptions,
  confirmLoading,
  onCancel,
  onSubmit,
  serviceLines,
}: HistoryEntryFormModalProps): JSX.Element {
  return (
    <Modal
      open={open}
      title={editingEntry ? 'Editar registro' : 'Registrar atención'}
      onCancel={onCancel}
      onOk={onSubmit}
      okText={editingEntry ? 'Guardar cambios' : 'Guardar'}
      cancelText="Cancelar"
      confirmLoading={confirmLoading}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item name="staff_user_id" hidden>
          <Input />
        </Form.Item>

        <Form.Item label="Profesional">
          <Form.Item
            shouldUpdate={(prev, current) => prev.staff_user_id !== current.staff_user_id}
            noStyle
          >
            {({ getFieldValue }) => {
              const selectedId = getFieldValue('staff_user_id') as number | undefined;
              const selectedLabel =
                staffOptions.find((option) => option.value === selectedId)?.label ??
                (selectedId ? `Staff #${selectedId}` : 'Sin profesional');

              return (
                <Tag color="cyan" style={{ fontSize: 14, padding: '4px 8px' }}>
                  {selectedLabel}
                </Tag>
              );
            }}
          </Form.Item>
        </Form.Item>

        <Form.Item label="Servicios">
          {serviceLines && serviceLines.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {serviceLines.map((line) => (
                <Tag color="blue" key={line.service_id} style={{ fontSize: 13, padding: '2px 6px', margin: 0 }}>
                  {line.service_name || `Servicio #${line.service_id}`}
                </Tag>
              ))}
            </div>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              Sin servicios registrados
            </Text>
          )}
        </Form.Item>

        <Form.Item
          label="Nota"
          name="notes"
          valuePropName="value"
          trigger="onChange"
          rules={[
            {
              validator: (_, value?: string | null) => {
                const plainTextLength = getRichTextPlainText(value).length;
                if (plainTextLength > NOTES_MAX_LENGTH) {
                  return Promise.reject(
                    new Error(`Máximo ${NOTES_MAX_LENGTH} caracteres`),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <SlateEditor
            initialValue=""
            toolbar="clinical"
            minHeight={140}
            placeholder="Notas de la atención"
            onChange={() => undefined}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default HistoryEntryFormModal;
